import { useState, useEffect } from "react";
import "@/App.css";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Search, Plus, Download, Edit, Trash2, Users, Ticket, Calendar, MapPin } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Dashboard = () => {
  const [activeTab, setActiveTab] = useState("reservations");
  
  // Reservations state
  const [reservations, setReservations] = useState([]);
  const [filteredReservations, setFilteredReservations] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isReservationDialogOpen, setIsReservationDialogOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState(null);
  const [stats, setStats] = useState({ total_reservations: 0, total_places: 0 });
  
  // Spectacles state
  const [spectacles, setSpectacles] = useState([]);
  const [filteredSpectacles, setFilteredSpectacles] = useState([]);
  const [spectacleSearchQuery, setSpectacleSearchQuery] = useState("");
  const [isSpectacleDialogOpen, setIsSpectacleDialogOpen] = useState(false);
  const [editingSpectacle, setEditingSpectacle] = useState(null);
  
  const [reservationFormData, setReservationFormData] = useState({
    nom: "",
    prenom: "",
    telephone: "",
    nombre_places: 1,
    commentaire: "",
    site_reseau: "",
    spectacle_id: ""
  });

  const [spectacleFormData, setSpectacleFormData] = useState({
    nom: "",
    date: "",
    lieu: "",
    description: ""
  });

  useEffect(() => {
    fetchReservations();
    fetchStats();
    fetchSpectacles();
  }, []);

  useEffect(() => {
    filterReservations();
  }, [searchQuery, reservations]);

  useEffect(() => {
    filterSpectacles();
  }, [spectacleSearchQuery, spectacles]);

  // Reservations functions
  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API}/reservations`);
      setReservations(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des réservations:", error);
      toast.error("Erreur lors du chargement des réservations");
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/reservations/stats/summary`);
      setStats(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des statistiques:", error);
    }
  };

  const filterReservations = () => {
    if (!searchQuery) {
      setFilteredReservations(reservations);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = reservations.filter(r => 
      r.nom?.toLowerCase().includes(query) ||
      r.prenom?.toLowerCase().includes(query) ||
      r.telephone?.toLowerCase().includes(query) ||
      r.spectacle_nom?.toLowerCase().includes(query) ||
      r.site_reseau?.toLowerCase().includes(query)
    );
    setFilteredReservations(filtered);
  };

  const handleReservationSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingReservation) {
        await axios.put(`${API}/reservations/${editingReservation.id}`, reservationFormData);
        toast.success("Réservation modifiée avec succès");
      } else {
        await axios.post(`${API}/reservations`, reservationFormData);
        toast.success("Réservation créée avec succès");
      }
      
      resetReservationForm();
      setIsReservationDialogOpen(false);
      fetchReservations();
      fetchStats();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEditReservation = (reservation) => {
    setEditingReservation(reservation);
    setReservationFormData({
      nom: reservation.nom,
      prenom: reservation.prenom,
      telephone: reservation.telephone,
      nombre_places: reservation.nombre_places,
      commentaire: reservation.commentaire || "",
      site_reseau: reservation.site_reseau || "",
      spectacle_id: reservation.spectacle_id || ""
    });
    setIsReservationDialogOpen(true);
  };

  const handleDeleteReservation = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cette réservation ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/reservations/${id}`);
      toast.success("Réservation supprimée");
      fetchReservations();
      fetchStats();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/reservations/export/csv`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'reservations.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Export réussi");
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors de l'export");
    }
  };

  const resetReservationForm = () => {
    setReservationFormData({
      nom: "",
      prenom: "",
      telephone: "",
      nombre_places: 1,
      commentaire: "",
      site_reseau: "",
      spectacle_id: ""
    });
    setEditingReservation(null);
  };

  // Spectacles functions
  const fetchSpectacles = async () => {
    try {
      const response = await axios.get(`${API}/spectacles`);
      setSpectacles(response.data);
    } catch (error) {
      console.error("Erreur lors du chargement des spectacles:", error);
      toast.error("Erreur lors du chargement des spectacles");
    }
  };

  const filterSpectacles = () => {
    if (!spectacleSearchQuery) {
      setFilteredSpectacles(spectacles);
      return;
    }

    const query = spectacleSearchQuery.toLowerCase();
    const filtered = spectacles.filter(s => 
      s.nom?.toLowerCase().includes(query) ||
      s.lieu?.toLowerCase().includes(query) ||
      s.date?.toLowerCase().includes(query)
    );
    setFilteredSpectacles(filtered);
  };

  const handleSpectacleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingSpectacle) {
        await axios.put(`${API}/spectacles/${editingSpectacle.id}`, spectacleFormData);
        toast.success("Spectacle modifié avec succès");
      } else {
        await axios.post(`${API}/spectacles`, spectacleFormData);
        toast.success("Spectacle créé avec succès");
      }
      
      resetSpectacleForm();
      setIsSpectacleDialogOpen(false);
      fetchSpectacles();
      fetchReservations(); // Refresh pour mettre à jour les noms de spectacles
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEditSpectacle = (spectacle) => {
    setEditingSpectacle(spectacle);
    setSpectacleFormData({
      nom: spectacle.nom,
      date: spectacle.date,
      lieu: spectacle.lieu,
      description: spectacle.description || ""
    });
    setIsSpectacleDialogOpen(true);
  };

  const handleDeleteSpectacle = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce spectacle ?")) {
      return;
    }

    try {
      await axios.delete(`${API}/spectacles/${id}`);
      toast.success("Spectacle supprimé");
      fetchSpectacles();
      fetchReservations();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  const resetSpectacleForm = () => {
    setSpectacleFormData({
      nom: "",
      date: "",
      lieu: "",
      description: ""
    });
    setEditingSpectacle(null);
  };

  return (
    <div className="app-container">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <div className="header-left">
            <div className="logo-section">
              <Ticket className="logo-icon" />
              <h1 data-testid="app-title">Gestion des Réservations</h1>
            </div>
            <p className="subtitle">Gérez vos spectacles et réservations en toute simplicité</p>
          </div>
          
          {/* Stats */}
          <div className="stats-container">
            <div className="stat-card">
              <Users className="stat-icon" />
              <div>
                <p className="stat-label">Réservations</p>
                <p className="stat-value" data-testid="total-reservations">{stats.total_reservations}</p>
              </div>
            </div>
            <div className="stat-card">
              <Ticket className="stat-icon" />
              <div>
                <p className="stat-label">Places totales</p>
                <p className="stat-value" data-testid="total-places">{stats.total_places}</p>
              </div>
            </div>
            <div className="stat-card">
              <Calendar className="stat-icon" />
              <div>
                <p className="stat-label">Spectacles</p>
                <p className="stat-value" data-testid="total-spectacles">{spectacles.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="tabs-container">
          <TabsList className="tabs-list">
            <TabsTrigger value="reservations" data-testid="tab-reservations" className="tab-trigger">
              <Ticket className="tab-icon" />
              Réservations
            </TabsTrigger>
            <TabsTrigger value="spectacles" data-testid="tab-spectacles" className="tab-trigger">
              <Calendar className="tab-icon" />
              Spectacles
            </TabsTrigger>
          </TabsList>

          {/* Reservations Tab */}
          <TabsContent value="reservations" className="tab-content">
            {/* Toolbar */}
            <div className="toolbar">
              <div className="search-box">
                <Search className="search-icon" />
                <Input
                  data-testid="search-reservations-input"
                  type="text"
                  placeholder="Rechercher par nom, prénom, téléphone, spectacle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="toolbar-actions">
                <Button
                  data-testid="export-button"
                  onClick={handleExport}
                  variant="outline"
                  className="btn-export"
                >
                  <Download className="btn-icon" />
                  Exporter CSV
                </Button>
                
                <Dialog open={isReservationDialogOpen} onOpenChange={(open) => {
                  setIsReservationDialogOpen(open);
                  if (!open) resetReservationForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-reservation-button" className="btn-primary">
                      <Plus className="btn-icon" />
                      Nouvelle réservation
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="dialog-content">
                    <DialogHeader>
                      <DialogTitle>{editingReservation ? "Modifier la réservation" : "Nouvelle réservation"}</DialogTitle>
                      <DialogDescription>
                        Remplissez les informations de la réservation
                      </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleReservationSubmit} className="form-content">
                      <div className="form-grid">
                        <div className="form-field">
                          <Label htmlFor="nom">Nom *</Label>
                          <Input
                            data-testid="form-nom"
                            id="nom"
                            value={reservationFormData.nom}
                            onChange={(e) => setReservationFormData({...reservationFormData, nom: e.target.value})}
                            required
                          />
                        </div>
                        
                        <div className="form-field">
                          <Label htmlFor="prenom">Prénom *</Label>
                          <Input
                            data-testid="form-prenom"
                            id="prenom"
                            value={reservationFormData.prenom}
                            onChange={(e) => setReservationFormData({...reservationFormData, prenom: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-grid">
                        <div className="form-field">
                          <Label htmlFor="telephone">Téléphone *</Label>
                          <Input
                            data-testid="form-telephone"
                            id="telephone"
                            value={reservationFormData.telephone}
                            onChange={(e) => setReservationFormData({...reservationFormData, telephone: e.target.value})}
                            required
                          />
                        </div>
                        
                        <div className="form-field">
                          <Label htmlFor="nombre_places">Nombre de places *</Label>
                          <Input
                            data-testid="form-nombre-places"
                            id="nombre_places"
                            type="number"
                            min="1"
                            value={reservationFormData.nombre_places}
                            onChange={(e) => setReservationFormData({...reservationFormData, nombre_places: parseInt(e.target.value)})}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="spectacle">Spectacle</Label>
                        <Select
                          value={reservationFormData.spectacle_id || "none"}
                          onValueChange={(value) => setReservationFormData({...reservationFormData, spectacle_id: value === "none" ? "" : value})}
                        >
                          <SelectTrigger data-testid="form-spectacle-select">
                            <SelectValue placeholder="Sélectionner un spectacle (optionnel)" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Aucun spectacle</SelectItem>
                            {spectacles.map((spectacle) => (
                              <SelectItem key={spectacle.id} value={spectacle.id}>
                                {spectacle.nom} - {spectacle.date}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="site_reseau">Site/Réseau</Label>
                        <Input
                          data-testid="form-site-reseau"
                          id="site_reseau"
                          value={reservationFormData.site_reseau}
                          onChange={(e) => setReservationFormData({...reservationFormData, site_reseau: e.target.value})}
                        />
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="commentaire">Commentaire</Label>
                        <Textarea
                          data-testid="form-commentaire"
                          id="commentaire"
                          value={reservationFormData.commentaire}
                          onChange={(e) => setReservationFormData({...reservationFormData, commentaire: e.target.value})}
                          rows={3}
                        />
                      </div>
                      
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsReservationDialogOpen(false)}>
                          Annuler
                        </Button>
                        <Button data-testid="form-submit-button" type="submit" className="btn-primary">
                          {editingReservation ? "Modifier" : "Créer"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Reservations Table */}
            <div className="table-container">
              <Table>
                <TableHeader>
                  <TableRow className="table-header-row">
                    <TableHead>Nom</TableHead>
                    <TableHead>Prénom</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead className="text-center">Places</TableHead>
                    <TableHead>Spectacle</TableHead>
                    <TableHead>Site/Réseau</TableHead>
                    <TableHead>Commentaire</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReservations.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center empty-state">
                        {searchQuery ? "Aucune réservation trouvée" : "Aucune réservation pour le moment"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredReservations.map((reservation) => (
                      <TableRow key={reservation.id} className="table-row" data-testid={`reservation-row-${reservation.id}`}>
                        <TableCell className="font-medium">{reservation.nom}</TableCell>
                        <TableCell>{reservation.prenom}</TableCell>
                        <TableCell>{reservation.telephone}</TableCell>
                        <TableCell className="text-center">
                          <span className="badge-places">{reservation.nombre_places}</span>
                        </TableCell>
                        <TableCell>{reservation.spectacle_nom || "-"}</TableCell>
                        <TableCell>{reservation.site_reseau || "-"}</TableCell>
                        <TableCell className="max-w-xs truncate">{reservation.commentaire || "-"}</TableCell>
                        <TableCell className="text-right">
                          <div className="action-buttons">
                            <Button
                              data-testid={`edit-reservation-button-${reservation.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditReservation(reservation)}
                              className="action-btn"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`delete-reservation-button-${reservation.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteReservation(reservation.id)}
                              className="action-btn delete-btn"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* Spectacles Tab */}
          <TabsContent value="spectacles" className="tab-content">
            {/* Toolbar */}
            <div className="toolbar">
              <div className="search-box">
                <Search className="search-icon" />
                <Input
                  data-testid="search-spectacles-input"
                  type="text"
                  placeholder="Rechercher par nom, lieu, date..."
                  value={spectacleSearchQuery}
                  onChange={(e) => setSpectacleSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
              
              <div className="toolbar-actions">
                <Dialog open={isSpectacleDialogOpen} onOpenChange={(open) => {
                  setIsSpectacleDialogOpen(open);
                  if (!open) resetSpectacleForm();
                }}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-spectacle-button" className="btn-primary">
                      <Plus className="btn-icon" />
                      Nouveau spectacle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="dialog-content">
                    <DialogHeader>
                      <DialogTitle>{editingSpectacle ? "Modifier le spectacle" : "Nouveau spectacle"}</DialogTitle>
                      <DialogDescription>
                        Remplissez les informations du spectacle
                      </DialogDescription>
                    </DialogHeader>
                    
                    <form onSubmit={handleSpectacleSubmit} className="form-content">
                      <div className="form-field">
                        <Label htmlFor="spectacle_nom">Nom du spectacle *</Label>
                        <Input
                          data-testid="form-spectacle-nom"
                          id="spectacle_nom"
                          value={spectacleFormData.nom}
                          onChange={(e) => setSpectacleFormData({...spectacleFormData, nom: e.target.value})}
                          required
                        />
                      </div>
                      
                      <div className="form-grid">
                        <div className="form-field">
                          <Label htmlFor="spectacle_date">Date *</Label>
                          <Input
                            data-testid="form-spectacle-date"
                            id="spectacle_date"
                            type="date"
                            value={spectacleFormData.date}
                            onChange={(e) => setSpectacleFormData({...spectacleFormData, date: e.target.value})}
                            required
                          />
                        </div>
                        
                        <div className="form-field">
                          <Label htmlFor="spectacle_lieu">Lieu *</Label>
                          <Input
                            data-testid="form-spectacle-lieu"
                            id="spectacle_lieu"
                            value={spectacleFormData.lieu}
                            onChange={(e) => setSpectacleFormData({...spectacleFormData, lieu: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="form-field">
                        <Label htmlFor="spectacle_description">Description</Label>
                        <Textarea
                          data-testid="form-spectacle-description"
                          id="spectacle_description"
                          value={spectacleFormData.description}
                          onChange={(e) => setSpectacleFormData({...spectacleFormData, description: e.target.value})}
                          rows={3}
                        />
                      </div>
                      
                      <DialogFooter>
                        <Button type="button" variant="outline" onClick={() => setIsSpectacleDialogOpen(false)}>
                          Annuler
                        </Button>
                        <Button data-testid="form-spectacle-submit-button" type="submit" className="btn-primary">
                          {editingSpectacle ? "Modifier" : "Créer"}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>

            {/* Spectacles Table */}
            <div className="table-container">
              <Table>
                <TableHeader>
                  <TableRow className="table-header-row">
                    <TableHead>Nom du spectacle</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Lieu</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSpectacles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center empty-state">
                        {spectacleSearchQuery ? "Aucun spectacle trouvé" : "Aucun spectacle pour le moment"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredSpectacles.map((spectacle) => (
                      <TableRow key={spectacle.id} className="table-row" data-testid={`spectacle-row-${spectacle.id}`}>
                        <TableCell className="font-medium">{spectacle.nom}</TableCell>
                        <TableCell>{spectacle.date}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <MapPin className="w-4 h-4 text-gray-500" />
                            {spectacle.lieu}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-xs truncate">{spectacle.description || "-"}</TableCell>
                        <TableCell className="text-right">
                          <div className="action-buttons">
                            <Button
                              data-testid={`edit-spectacle-button-${spectacle.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditSpectacle(spectacle)}
                              className="action-btn"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              data-testid={`delete-spectacle-button-${spectacle.id}`}
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteSpectacle(spectacle.id)}
                              className="action-btn delete-btn"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
